import sortBy from "lodash.sortby"
import { isProxy, isReactive, isRef, toRaw } from "vue"

// Originally from https://github.com/vuejs/core/issues/5303#issuecomment-1543596383
export function deepToRaw<T extends Record<string, any>>(sourceObj: T): T {
	const objectIterator = (input: any): any => {
		if (Array.isArray(input)) {
			return input.map(item => objectIterator(item))
		}
		if (isRef(input) || isReactive(input) || isProxy(input)) {
			return objectIterator(toRaw(input))
		}
		if (input && typeof input === "object") {
			return Object.keys(input).reduce((acc, key) => {
				acc[key as keyof typeof acc] = objectIterator(input[key])
				return acc
			}, {} as T)
		}
		return input
	}

	return objectIterator(sourceObj)
}

export function areEqualIgnoringOrder(a: unknown, b: unknown): boolean {
	const normalize = (value: unknown) => JSON.stringify(Array.isArray(value) ? sortBy(value) : value)
	return normalize(a) === normalize(b)
}

function isPrimitive(value: unknown): boolean {
	return value === null || ["string", "number", "boolean"].includes(typeof value)
}

function isPlainObject(value: unknown): value is Record<string, any> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

// `typeof null` is `"object"`, so nullable scalar filters cannot use the generic scalar merge rule.
function mergeNullableDefault<T>(defaultVal: T, storedVal: unknown): T {
	return isPrimitive(storedVal) ? (storedVal as T) : defaultVal
}

function mergeDateDefault(defaultVal: Date, storedVal: unknown): Date {
	if (typeof storedVal === "string" || typeof storedVal === "number") {
		const date = new Date(storedVal)
		return isNaN(date.getTime()) ? defaultVal : date
	}

	if (storedVal instanceof Date) {
		return storedVal
	}

	return defaultVal
}

function mergeArrayDefault<T>(defaultVal: T[], storedVal: unknown): T[] {
	return Array.isArray(storedVal) ? storedVal : defaultVal
}

function mergeObjectDefault<T extends Record<string, any>>(defaultVal: T, storedVal: unknown): T {
	return isPlainObject(storedVal) ? mergeWithDefaults(defaultVal, storedVal) : defaultVal
}

function mergeScalarDefault<T>(defaultVal: T, storedVal: unknown): T {
	return storedVal === null || typeof storedVal === typeof defaultVal ? (storedVal as T) : defaultVal
}

// deep merge of locally stored values with default values
export function mergeWithDefaults<T extends Record<string, any>>(defaults: T, stored: Record<string, any>): T {
	const result = {} as Record<string, any>

	for (const key of Object.keys(defaults)) {
		const defaultVal = defaults[key]
		const hasStoredKey = Object.prototype.hasOwnProperty.call(stored, key)
		const storedVal = stored[key]

		// if key in default but missing from stored, use defaults
		if (!hasStoredKey || storedVal === undefined) {
			result[key] = defaultVal
			continue
		}

		// if default is nullable, use special care
		if (defaultVal === null) {
			result[key] = mergeNullableDefault(defaultVal, storedVal)
			continue
		}

		// dates are reconstructed as date objects
		if (defaultVal instanceof Date) {
			result[key] = mergeDateDefault(defaultVal, storedVal)
			continue
		}

		// if arrays, validate stored value is also an array
		if (Array.isArray(defaultVal)) {
			result[key] = mergeArrayDefault(defaultVal, storedVal)
			continue
		}

		// if objects, deep merge recursively and fallback if type mismatch
		if (isPlainObject(defaultVal)) {
			result[key] = mergeObjectDefault(defaultVal, storedVal)
			continue
		}

		result[key] = mergeScalarDefault(defaultVal, storedVal)
	}

	return result as T
}
